import * as Y from 'yjs';
import {
    NodeInstance,
    DataConnection,
    GlueVariable,
    FunctionDefinition,
    FunctionPortDefinition,
    isFunctionNodeInstance,
} from '@glueapp/graph-types';
import {
    NODE_ID,
    NODE_LEGACY_INT_ID,
    NODE_NODE_ID,
    NODE_NODE_VERSION,
    NODE_POSITION,
    NODE_POSITION_X,
    NODE_POSITION_Y,
    NODE_WIDTH,
    NODE_HEIGHT,
    NODE_SELECTED_INTEGRATION_ACCESS,
    NODE_SELECTED_VARIABLE,
    NODE_FUNCTION_ID,
    NODE_PORT_VALUES,
    NODE_PORT_USER_DEFINED_TYPES,
    NODE_STATE,
    CONNECTION_ID,
    CONNECTION_LEGACY_INT_ID,
    CONNECTION_SENDING_NODE_ID,
    CONNECTION_SENDING_PORT_NAME,
    CONNECTION_SENDING_PORT_TYPE,
    CONNECTION_RECEIVING_NODE_ID,
    CONNECTION_RECEIVING_PORT_NAME,
    CONNECTION_RECEIVING_PORT_TYPE,
    VARIABLE_NAME,
    VARIABLE_TYPE_DETERMINATION,
    VARIABLE_TYPE,
    VARIABLE_RESOLVED_TYPE,
    FUNCTION_ID,
    FUNCTION_NAME,
    FUNCTION_DESCRIPTION,
    FUNCTION_NODES,
    FUNCTION_CONNECTIONS,
    FUNCTION_INPUT_PORT_DEFINITIONS,
    FUNCTION_OUTPUT_PORT_DEFINITIONS,
    FUNCTION_INPUTS_NODE_ID,
    FUNCTION_OUTPUTS_NODE_ID,
    FUNCTION_CREATED_AT,
    FUNCTION_UPDATED_AT,
    FUNCTION_VERSION,
} from './yDocSchema';
import { fillEncodedDataValueMap } from './dataValueCodec';

/**
 * Encoders that turn a `DataGlueGraph` value into the corresponding Y.Map / Y.Array
 * structure used in the doc. Shared between `jsonToYDoc` (full-graph hydration) and
 * `yDocMutator` (single-action mutations) so the two stay in lockstep.
 */

export interface IdMap {
    /** Maps the legacy integer NodeInstance id -> UUID. */
    nodes: Map<number, string>;
}

export function hydrateNodesAndConnections(
    nodesTarget: Y.Map<Y.Map<unknown>>,
    connectionsTarget: Y.Map<Y.Map<unknown>>,
    nodeInstances: NodeInstance[],
    connections: DataConnection[],
    mintId: () => string,
): IdMap {
    const idMap: IdMap = { nodes: new Map() };

    for (const node of nodeInstances) {
        const uuid = mintId();
        idMap.nodes.set(node.id, uuid);
        nodesTarget.set(uuid, encodeNode(node, uuid));
    }

    for (const connection of connections) {
        const uuid = mintId();
        const sendingUuid = idMap.nodes.get(connection.sendingNodeInstanceId);
        const receivingUuid = idMap.nodes.get(connection.receivingNodeInstanceId);
        if (sendingUuid === undefined || receivingUuid === undefined) {
            // Connection references a node not present in this scope. Skip it
            // rather than write a dangling reference.
            continue;
        }
        connectionsTarget.set(uuid, encodeConnection(connection, uuid, sendingUuid, receivingUuid));
    }

    return idMap;
}

export function encodeNode(node: NodeInstance, uuid: string): Y.Map<unknown> {
    const yNode = new Y.Map<unknown>();
    yNode.set(NODE_ID, uuid);
    yNode.set(NODE_LEGACY_INT_ID, node.id);
    yNode.set(NODE_NODE_ID, node.nodeId);
    if (node.nodeVersion !== undefined) yNode.set(NODE_NODE_VERSION, node.nodeVersion);

    const position = new Y.Map<number>();
    position.set(NODE_POSITION_X, node.position.x);
    position.set(NODE_POSITION_Y, node.position.y);
    yNode.set(NODE_POSITION, position);

    if (node.width !== undefined) yNode.set(NODE_WIDTH, node.width);
    if (node.height !== undefined) yNode.set(NODE_HEIGHT, node.height);

    if (node.selectedIntegrationAccess) {
        yNode.set(NODE_SELECTED_INTEGRATION_ACCESS, JSON.stringify(node.selectedIntegrationAccess));
    }
    if (node.selectedVariable !== undefined) yNode.set(NODE_SELECTED_VARIABLE, node.selectedVariable);

    if (isFunctionNodeInstance(node)) {
        yNode.set(NODE_FUNCTION_ID, node.functionId);
    }

    const portValues = new Y.Map<Y.Map<unknown>>();
    fillEncodedDataValueMap(portValues, node.portValues);
    yNode.set(NODE_PORT_VALUES, portValues);

    const portUserDefinedTypes = new Y.Map<unknown>();
    for (const [portName, userDefinedType] of Object.entries(node.portUserDefinedTypes ?? {})) {
        portUserDefinedTypes.set(portName, JSON.stringify(userDefinedType));
    }
    yNode.set(NODE_PORT_USER_DEFINED_TYPES, portUserDefinedTypes);

    const state = new Y.Map<Y.Map<unknown>>();
    fillEncodedDataValueMap(state, node.state);
    yNode.set(NODE_STATE, state);

    return yNode;
}

export function encodeConnection(connection: DataConnection, uuid: string, sendingUuid: string, receivingUuid: string): Y.Map<unknown> {
    const yConn = new Y.Map<unknown>();
    yConn.set(CONNECTION_ID, uuid);
    yConn.set(CONNECTION_LEGACY_INT_ID, connection.id);
    yConn.set(CONNECTION_SENDING_NODE_ID, sendingUuid);
    yConn.set(CONNECTION_SENDING_PORT_NAME, connection.sendingPortName);
    yConn.set(CONNECTION_SENDING_PORT_TYPE, connection.sendingPortType);
    yConn.set(CONNECTION_RECEIVING_NODE_ID, receivingUuid);
    yConn.set(CONNECTION_RECEIVING_PORT_NAME, connection.receivingPortName);
    yConn.set(CONNECTION_RECEIVING_PORT_TYPE, connection.receivingPortType);
    return yConn;
}

export function encodeVariable(variable: GlueVariable): Y.Map<unknown> {
    const yVar = new Y.Map<unknown>();
    yVar.set(VARIABLE_NAME, variable.name);
    yVar.set(VARIABLE_TYPE_DETERMINATION, variable.typeDetermination);
    if (variable.type !== undefined) yVar.set(VARIABLE_TYPE, JSON.stringify(variable.type));
    if (variable.resolvedType !== undefined) yVar.set(VARIABLE_RESOLVED_TYPE, JSON.stringify(variable.resolvedType));
    return yVar;
}

export function encodeFunction(fn: FunctionDefinition, mintId: () => string): Y.Map<unknown> {
    const yFn = new Y.Map<unknown>();
    yFn.set(FUNCTION_ID, fn.id);
    yFn.set(FUNCTION_NAME, fn.name);
    if (fn.description !== undefined) yFn.set(FUNCTION_DESCRIPTION, fn.description);
    yFn.set(FUNCTION_CREATED_AT, toIsoString(fn.createdAt));
    yFn.set(FUNCTION_UPDATED_AT, toIsoString(fn.updatedAt));
    yFn.set(FUNCTION_VERSION, fn.version);

    const fnNodes = new Y.Map<Y.Map<unknown>>();
    const fnConnections = new Y.Map<Y.Map<unknown>>();
    const idMap = hydrateNodesAndConnections(fnNodes, fnConnections, fn.nodeInstances, fn.connections, mintId);
    yFn.set(FUNCTION_NODES, fnNodes);
    yFn.set(FUNCTION_CONNECTIONS, fnConnections);

    const fnInputsUuid = idMap.nodes.get(fn.functionInputsNodeId);
    const fnOutputsUuid = idMap.nodes.get(fn.functionOutputsNodeId);
    if (fnInputsUuid === undefined || fnOutputsUuid === undefined) {
        throw new Error(
            `encodeFunction: function "${fn.id}" references functionInputsNodeId/functionOutputsNodeId not present in its nodeInstances`,
        );
    }
    yFn.set(FUNCTION_INPUTS_NODE_ID, fnInputsUuid);
    yFn.set(FUNCTION_OUTPUTS_NODE_ID, fnOutputsUuid);

    const inputs = new Y.Array<Y.Map<unknown>>();
    inputs.push(fn.inputPortDefinitions.map(encodeFunctionPortDefinition));
    yFn.set(FUNCTION_INPUT_PORT_DEFINITIONS, inputs);

    const outputs = new Y.Array<Y.Map<unknown>>();
    outputs.push(fn.outputPortDefinitions.map(encodeFunctionPortDefinition));
    yFn.set(FUNCTION_OUTPUT_PORT_DEFINITIONS, outputs);

    return yFn;
}

export function encodeFunctionPortDefinition(port: FunctionPortDefinition): Y.Map<unknown> {
    const yPort = new Y.Map<unknown>();
    yPort.set('id', port.id);
    yPort.set('name', port.name);
    yPort.set('type', JSON.stringify(port.type));
    if (port.optional !== undefined) yPort.set('optional', port.optional);
    if (port.description !== undefined) yPort.set('description', port.description);
    return yPort;
}

export function toIsoString(value: Date | string): string {
    return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}
